import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const FIELD_CLS =
  "h-12 rounded-none border-[#2A2A2A] bg-[#0A0A0A] text-[#F5F5F5] placeholder:text-[#5C636A] focus-visible:ring-1 focus-visible:ring-[#C5A059] focus-visible:ring-offset-0";

const Label = ({ children }) => (
  <span className="mb-2 block font-manrope text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8A9199]">
    {children}
  </span>
);

export const ContactForm = () => {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ company: "", name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/trial-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(t.contact.success);
      setForm({ company: "", name: "", phone: "", email: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, delay: 0.15, ease: EASE }}
      data-testid="contact-form"
      onSubmit={onSubmit}
      className="relative overflow-hidden border border-[#2A2A2A] bg-[#111111] p-8 md:p-10"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#C5A059]/10 blur-3xl" />
      <div className="relative grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <Label>{t.contact.company}</Label>
          <Input
            data-testid="contact-input-company"
            required
            minLength={2}
            value={form.company}
            onChange={set("company")}
            placeholder={t.contact.companyPh}
            className={FIELD_CLS}
          />
        </div>
        <div>
          <Label>{t.contact.name}</Label>
          <Input
            data-testid="contact-input-name"
            required
            minLength={2}
            value={form.name}
            onChange={set("name")}
            placeholder={t.contact.namePh}
            className={FIELD_CLS}
          />
        </div>
        <div>
          <Label>{t.contact.phone}</Label>
          <Input
            data-testid="contact-input-phone"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder={t.contact.phonePh}
            className={FIELD_CLS}
          />
        </div>
        <div>
          <Label>{t.contact.email}</Label>
          <Input
            data-testid="contact-input-email"
            required
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder={t.contact.emailPh}
            className={FIELD_CLS}
          />
        </div>
      </div>
      <button
        data-testid="contact-submit-button"
        type="submit"
        disabled={loading}
        className="group relative mt-8 inline-flex w-full items-center justify-center gap-3 bg-[#C5A059] px-8 py-4 font-manrope text-sm font-bold uppercase tracking-[0.14em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#B38E4A] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        )}
        {loading ? t.contact.sending : t.contact.submit}
      </button>
      <p className="mt-4 font-manrope text-xs leading-relaxed text-[#5C636A]">
        {t.contact.privacy}
      </p>
    </motion.form>
  );
};
